import _ from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { SideNavigation, Badge } from "@toddle-edu/ds-web";
import { useTranslation } from "react-i18next";
import { ChevronRightOutlined } from "@toddle-edu/ds-icons";

import classes from "../TagLearningGoalsList.scss";
import { onEnterOrSpaceKeyDown } from "Utils";
import {
  getIsNewGradingSetupEnabled,
  getIsStandardsV2,
} from "../../../../../../../utils";
import { STANDARD_UIDS } from "modules/Services/ServicesConstants";

const SideBarNavigation = props => {
  const {
    filteredNodes,
    selectedNodeId,
    getCurrentSelectedElement,
    isLocale = true,
    identifier,
    getSelectedCount,
    searchTerm,
    selectAllFromNav,
    isNavItemSelected = id => {},
    parent = "",
    isPrimaryNav = true,
    standardMapValue = undefined,
    standardLabel = "Standards",
  } = props;
  const { t } = useTranslation(["journal", "common"]);
  const widthValue =
    getIsStandardsV2() || getIsNewGradingSetupEnabled() ? "20%" : "25%";
  const styles = {
    sidebarContainer: {
      height: "100%",
      maxWidth: widthValue,
      width: widthValue,
    },
  };
  const [id, setId] = useState(
    selectedNodeId ? selectedNodeId : filteredNodes?.[0]?.value
  );
  useEffect(() => {
    setId(selectedNodeId);
  }, [selectedNodeId]);

  const getItemLabel = ({ isLocale, label }) => {
    if (isLocale && /^(\w+):(\w+)$/i.test(label)) {
      return t(label);
    } else {
      return label;
    }
  };
  const [sideNavOptionsState, setSideNavOptionsState] = useState(
    _.map(filteredNodes, item => ({
      key: _.get(item, identifier),
      isChecked: isNavItemSelected({ uid: _.get(item, identifier), parent }),
      isIndeterminate: false,
    }))
  );
  useEffect(() => {
    setSideNavOptionsState(
      _.map(filteredNodes, item => ({
        key: _.get(item, identifier),
        isChecked:
          isNavItemSelected({ uid: _.get(item, identifier), parent }) ==
          "CHECKED",
        isIndeterminate:
          isNavItemSelected({ uid: _.get(item, identifier), parent }) ==
          "INDETERMINATE",
      }))
    );
  }, [filteredNodes]);

  const getSideNavOptionsState = key => {
    return _.find(sideNavOptionsState, { key })?.isChecked;
  };

  const onChevronLeftClick = item => () => {
    setId(item);
    getCurrentSelectedElement && getCurrentSelectedElement(item);
  };

  const onHandleKeyDown = (e, item) => {
    onEnterOrSpaceKeyDown(e, () => {
      onChevronLeftClick(item)();
    });
  };

  const createKeyDownHandler = item => e => {
    onHandleKeyDown(e, item);
  };
  const sideBarOptions = useMemo(() => {
    const options = [];
    _.map(filteredNodes, item => {
      const count = getSelectedCount(_.get(item, identifier), parent);

      const isPrimaryNav = _.get(item, "isPrimaryNav", true);
      const isSearchTermPresent = _.size(searchTerm) > 0;

      const itemLabel = getItemLabel({
        isLocale,
        label: _.get(item, "label"),
        count,
        isSearchTermPresent,
      });

      const title = _.includes(_.values(STANDARD_UIDS), _.get(item, identifier))
        ? standardLabel ?? itemLabel
        : itemLabel;

      options.push({
        key: _.get(item, identifier),
        title,
        tier: 1,
        parent: "root",
        children: [],
        suffix: (
          <>
            <span
              style={{
                marginRight: "8px",
                marginTop: "-4px",
                visibility: count ? "visible" : "hidden",
              }}
            >
              <Badge
                type="numeric"
                size={"medium"}
                variant="filter"
                value={count}
              />
            </span>
            {isPrimaryNav ||
            _.size(_.get(item, "children", []), 0) > 0 ||
            (!_.isUndefined(standardMapValue) && getIsNewGradingSetupEnabled()
              ? _.get(standardMapValue, item?.id) > 0
              : false) ? (
              <span
                className={classes.learningGoalSelector}
                tabIndex={0}
                onClick={onChevronLeftClick(_.get(item, identifier))}
                onKeyDown={createKeyDownHandler(_.get(item, identifier))}
              >
                <ChevronRightOutlined />
              </span>
            ) : null}
          </>
        ),
        haveCheckbox: true,
      });
    });
    return [
      {
        key: "root",
        children: [
          ..._.map(options, option => _.get(option, "key", undefined)),
        ],
        tier: 1,
      },
      ...options,
    ];
  }, [filteredNodes]);

  return (
    <SideNavigation
      theme="white"
      options={sideBarOptions}
      onClick={e => {
        setId(e);
        getCurrentSelectedElement && getCurrentSelectedElement(e);
      }}
      containerStyle={styles.sidebarContainer}
      selectedOptionKey={id}
      rootKey={"root"}
      haveCheckbox={true}
      onCheckboxClick={e => {
        selectAllFromNav(e, !getSideNavOptionsState(e), parent);
      }}
      checkboxesState={sideNavOptionsState}
      showCheckboxesForAll={true}
      padding={"0px"}
      testId={`journal-postcreation-taglearninggoals-${
        isPrimaryNav ? "primary" : "secondary"
      }`}
    />
  );
};

export default SideBarNavigation;
